/* eslint-disable @typescript-eslint/no-floating-promises, ember/no-runloop */
import type Store from '@ember-data/store';
import type RouterService from '@ember/routing/router-service';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import DraftManager from 'parog-web/components/forc/draft-manager';
import FormAction from 'parog-web/components/forc/form-action';
import type Patient from 'parog-web/models/patient';
import { Changeset } from 'ember-changeset';
import { task, timeout } from 'ember-concurrency';
import type AnalyticsService from 'parog-web/services/analytics';
import type SemeiaSessionService from 'parog-web/services/session';
import MessageFeed from 'parog-web/components/chat/message-feed';
import ChatInput from 'parog-web/components/chat/input';
import and from 'ember-truth-helpers/helpers/and';
import Buttons from 'parog-web/components/chat/buttons';
import changesetGet from 'ember-changeset/helpers/changeset-get';
import type Owner from '@ember/owner';

interface Args {
  patient: Patient;
  isAutoFocusEnabled: boolean;
}

const messageContentKey = 'messageContent';

export default class ChatContentComponent extends Component<Args> {
  @service declare analytics: AnalyticsService;
  @service declare router: RouterService;
  @service declare session: SemeiaSessionService;
  @service declare store: Store;

  /*** MESSAGE CONTENT DRAFT MANAGER ***/
  changeset = Changeset({});
  public formAction = FormAction.New;
  public fullComponentId = 'chat/content';
  public shouldAutoSaveDraft = true;

  get isMessagingRestricted(): boolean {
    return (
      Boolean(this.session.patient) &&
      this.args.patient.healthCentre.hasRestrictedMessaging
    );
  }

  public get patient(): Patient {
    return this.args.patient;
  }

  get shouldShowInput(): boolean {
    if (this.session.medic) {
      return true;
    }

    return (
      !this.isMessagingRestricted ||
      (this.isMessagingRestricted &&
        Boolean(this.changeset.get(messageContentKey)))
    );
  }

  #messageContentDraft = new DraftManager(this);

  get #cleanedMessage(): string {
    return this.changeset.get(messageContentKey).trim();
  }

  get #isMessageValid(): boolean {
    return Boolean(this.#cleanedMessage) && this.#cleanedMessage !== '';
  }

  constructor(owner: Owner, args: Args) {
    super(owner, args);

    this.changeset.on('afterValidation', () => {
      this.#messageContentDraft.onDidUpdate();
    });

    const hasDraft = DraftManager.hasDraftFor(
      DraftManager.localStorageKey(this.fullComponentId, this.patient)
    );

    if (hasDraft) {
      next(() => {
        this.#messageContentDraft.load();
      });
    } else {
      next(() => {
        this.changeset.set(messageContentKey, '');
      });
    }

    if (this.args.isAutoFocusEnabled && !this.isMessagingRestricted) {
      this.#focus();
    }

    // Process all chat messages notifs for this patient
    // if we are rendering this component when logged as a medic
    // HACK: This needs to be run in the next runloop to avoid simultaneous read+write on tracked objects
    // this is probably a code smell!
    if (this.session.medic)
      next(args.patient, args.patient.processChatNotifications);

    setTimeout(this.#scrollToBottom, 0);
  }

  /**
   * Can be called either from medics web or patient web
   *
   * So the transition & callback URL differs according
   * to the current user type
   */
  onFileUploadClicked = (): void => {
    const editionCallbackUrl = this.session.medic
      ? `/medics-web/patients/${this.args.patient.id}/chat`
      : '/patient-web/chat';

    if (this.session.medic) {
      this.router.transitionTo({
        queryParams: {
          tab: 'upload-document'
        }
      });
    } else {
      this.router.transitionTo(
        `patient-web.protected.feature.component`,
        'document',
        'edition-form',
        {
          queryParams: {
            callbackUrl: editionCallbackUrl
          }
        }
      );
    }
  };

  /**
   * Can be called only on patient web
   */
  onMedicalEventClicked = (): void => {
    this.router.transitionTo(
      `patient-web.protected.feature.component`,
      'medical-event',
      'edition-form',
      {
        queryParams: {
          callbackUrl: '/patient-web/chat'
        }
      }
    );
  };

  onSendMessage = (): void => {
    this.sendMessageTask.perform();
    this.#focus();
  };

  onSuggestionSelected = (selectedSuggestion: string): void => {
    let message;
    switch (selectedSuggestion) {
      case 'medical-prescription':
        message =
          'Bonjour, mon ordonnance sur la plateforme n’est plus à jour et/ou j’ai besoin d’une nouvelle ordonnance de médicaments.';
        break;
      case 'exam-prescription':
        message =
          'Bonjour, mon ordonnance sur la plateforme n’est plus à jour et/ou j’ai besoin d’une nouvelle ordonnance d’examens médicaux.';
        break;
      case 'bio-prescription':
        message =
          'Bonjour, mon ordonnance sur la plateforme n’est plus à jour et/ou j’ai besoin d’une nouvelle ordonnance d’examen biologiques.';
        break;
      default:
        break;
    }

    this.changeset.set(messageContentKey, message);
    this.scrollAndFocus();
  };

  scrollAndFocus = (): void => {
    setTimeout(this.#scrollToBottom, 0);
    if (!this.isMessagingRestricted) {
      this.#focus();
    }
  };

  sendMessageTask = task({ enqueue: true }, async () => {
    if (!this.#isMessageValid) return;

    const messageEvent = this.store.createRecord(
      'patient-chat-message-sent-journal-event',
      {
        patient: this.args.patient,
        creator: this.session.medic || this.session.patient,
        occurredAt: new Date(),
        body: this.#cleanedMessage
      }
    );

    await messageEvent.save();
    this.analytics.trackJeCreation(messageEvent);

    this.changeset.set(messageContentKey, '');
    this.#messageContentDraft.onDidSave();
    // setTimeout is needed otherwise it scrolls before the dom is updated
    setTimeout(this.#scrollToBottom, 0);

    // Helps the user understanding that something is happening.
    await timeout(200);
  });

  #focus(): void {
    (document.querySelector('.ui.input') as HTMLInputElement).focus();
  }

  #scrollToBottom(): void {
    const element = document.querySelector('[data-test-sidebar-content=""]');
    if (element) {
      const offset = element.scrollHeight - element.clientHeight;
      element.scrollTo(0, offset);
    } else {
      window.scrollTo(0, document.body.scrollHeight);
    }
  }

  <template>
    <div
      class='[&_.sender.message_.content]:bg-grey-medium m-0 p-0 [&_.message]:mb-4 [&_.message]:text-left [&_.message_.content]:w-[fit-content] [&_.message_.content]:max-w-[60%] [&_.message_.content]:break-words [&_.message_.content]:rounded-[10px] [&_.message_.content]:px-[14px] [&_.message_.content]:py-[10px] [&_.message_.pre-line]:whitespace-pre-line [&_.message_.signature]:max-w-[100%] [&_.message_.signature]:px-[5px] [&_.message_.signature]:py-0 [&_.message_.unread]:text-[0.85714286em] [&_.recipient-alternate_.content]:bg-[#808080] [&_.recipient-alternate_.content]:text-white [&_.recipient.message_.content]:bg-[#d3d3d3] [&_.recipient.message_.content]:text-[#1c1d1e] [&_.recipient]:flex [&_.recipient]:flex-col [&_.recipient]:items-start [&_.recipient]:justify-center [&_.sender.message_.content]:text-[#1c1d1e] [&_.sender]:flex [&_.sender]:flex-col [&_.sender]:items-end [&_.sender]:justify-center [&_.ui.button]:mx-2 [&_.ui.form_textarea.ui.input]:mt-0 [&_.ui.form_textarea.ui.input]:w-full'
    >
      <MessageFeed
        @patient={{@patient}}
        @scrollAndFocus={{this.scrollAndFocus}}
      />

      {{#if this.session.patient}}
        <span class='semeia secondary-color'>Ceci n'est pas un canal d'urgence.
          Appelez le 112 ou le 15 pour les urgences.</span>
      {{/if}}

      {{#if this.shouldShowInput}}
        <ChatInput
          @changeset={{this.changeset}}
          @isMessagingRestricted={{this.isMessagingRestricted}}
        />
      {{/if}}

      {{#if
        (and this.session.medic @patient.healthCentre.hasRestrictedMessaging)
      }}
        <span class='semeia primary-color bold'>Attention votre patient ne peut
          pas répondre librement à vos questions !</span>
      {{/if}}

      <Buttons
        @isMessageBeingSent={{this.sendMessageTask.isRunning}}
        @onSuggestionSelected={{this.onSuggestionSelected}}
        @onFileUploadClicked={{this.onFileUploadClicked}}
        @onMedicalEventClicked={{this.onMedicalEventClicked}}
        @onSendMessage={{this.onSendMessage}}
        @messageContent={{changesetGet this.changeset 'messageContent'}}
      />
    </div>
  </template>
}
